import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

const normalizeOption = (option) => {
  if (typeof option === 'string' || typeof option === 'number') {
    return { value: String(option), label: String(option) };
  }
  if (Array.isArray(option)) {
    return { value: String(option[0]), label: option[1] };
  }
  return { ...option, value: String(option.value) };
};

function PremiumSelect({
  name = '',
  value = '',
  options = [],
  onChange,
  disabled = false,
  ariaLabel,
  placeholder = 'Select an option',
  className = '',
  compact = false,
  menuPlacement = 'bottom',
}) {
  const normalizedOptions = useMemo(() => options.map(normalizeOption), [options]);
  const hasRichOptions = normalizedOptions.some((option) => option.description || option.badge);
  const selectedValue = String(value ?? '');
  const selectedIndex = normalizedOptions.findIndex((option) => option.value === selectedValue);
  const selectedOption = normalizedOptions[selectedIndex];
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(Math.max(0, selectedIndex));
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const listboxId = useId();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) optionRefs.current[activeIndex]?.focus();
  }, [activeIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) setActiveIndex(Math.max(0, selectedIndex));
  }, [isOpen, selectedIndex]);

  const openMenu = (index = Math.max(0, selectedIndex)) => {
    if (disabled || normalizedOptions.length === 0) return;
    setActiveIndex(index);
    setIsOpen(true);
  };

  const choose = (option) => {
    onChange?.({ target: { name, value: option.value } });
    setIsOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const moveActive = (direction) => {
    setActiveIndex((current) => (current + direction + normalizedOptions.length) % normalizedOptions.length);
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openMenu(event.key === 'ArrowDown' ? Math.max(0, selectedIndex) : normalizedOptions.length - 1);
    }
  };

  const handleOptionKeyDown = (event, option) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setActiveIndex(event.key === 'Home' ? 0 : normalizedOptions.length - 1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(option);
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      setIsOpen(false);
      if (event.key === 'Escape') {
        event.preventDefault();
        triggerRef.current?.focus();
      }
    }
  };

  return (
    <div
      ref={rootRef}
      className={`cs-select ${compact ? 'is-compact' : ''} ${hasRichOptions ? 'has-rich-options' : ''} ${className}`.trim()}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`cs-select-trigger ${isOpen ? 'open' : ''}`}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        disabled={disabled}
        onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className={`cs-select-value ${selectedOption ? '' : 'placeholder'} ${selectedOption?.description ? 'has-description' : ''}`}>
          <strong>{selectedOption?.label || placeholder}</strong>
          {selectedOption?.description && <small>{selectedOption.description}</small>}
        </span>
        {selectedOption?.badge && <em className="cs-select-badge">{selectedOption.badge}</em>}
        <ChevronDown size={16} className="cs-select-chevron" aria-hidden="true" />
      </button>
      {isOpen && (
        <div
          id={listboxId}
          className={`cs-select-menu compact-scroll-box ${menuPlacement === 'top' ? 'opens-up' : ''}`}
          role="listbox"
          aria-label={ariaLabel || name.replaceAll('_', ' ') || 'Options'}
        >
          {normalizedOptions.map((option, index) => (
            <button
              ref={(element) => { optionRefs.current[index] = element; }}
              type="button"
              role="option"
              aria-selected={selectedValue === option.value}
              tabIndex={activeIndex === index ? 0 : -1}
              key={option.value}
              className={`cs-select-option ${selectedValue === option.value ? 'selected' : ''} ${activeIndex === index ? 'focused' : ''}`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(option)}
              onKeyDown={(event) => handleOptionKeyDown(event, option)}
            >
              <span className="cs-select-option-copy">
                <strong>{option.label}</strong>
                {option.description && <small>{option.description}</small>}
              </span>
              <span className="cs-select-option-side">
                {option.badge && <em className="cs-select-badge">{option.badge}</em>}
                {selectedValue === option.value && <Check size={15} aria-hidden="true" />}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default PremiumSelect;
